/**
 * Toolbar shape style and last-used edge style persisted in `sessionStorage` (ahooks).
 * Feeds shape/connector placement and default data for new edges.
 */

import { useSessionStorageState } from "ahooks";
import { useMemo } from "react";
import type { EdgeStyle } from "../nodes/EdgeFloatingToolbar";
import { ALL_SHAPE_TYPES, type ShapeType } from "../nodes/shapes/index";
import {
  DEFAULT_EDGE_COLOR,
  DEFAULT_SHAPE_COLOR,
  SHAPE_COLORS,
} from "../nodes/shared/CanvasColorPalettes";

export type ShapeStrokeStyle = "solid" | "dashed" | "dotted" | "none";

/** Stroke styles for lines, edges, connectors, and sections (no invisible stroke). */
export type LineStrokeStyle = Exclude<ShapeStrokeStyle, "none">;

export type ShapeStyleState = {
  color: string;
  noFill: boolean;
  strokeStyle: ShapeStrokeStyle;
  strokeWidth: number;
  shape: ShapeType;
};

export const DEFAULT_SHAPE_STYLE: ShapeStyleState = {
  color: DEFAULT_SHAPE_COLOR,
  noFill: false,
  strokeStyle: "solid",
  strokeWidth: 2,
  shape: "rectangle",
};

export type LastEdgeStyle = Required<
  Pick<
    EdgeStyle,
    | "color"
    | "stroke_style"
    | "stroke_width"
    | "curve_type"
    | "start_arrow"
    | "end_arrow"
  >
>;

export const DEFAULT_EDGE_STYLE: LastEdgeStyle = {
  color: DEFAULT_EDGE_COLOR,
  stroke_style: "solid",
  stroke_width: 2,
  curve_type: "step",
  start_arrow: "none",
  end_arrow: "arrow",
};

const SHAPE_STYLE_KEY = "canvas:shapeStyle";
const EDGE_STYLE_KEY = "canvas:edgeStyle";

function deserializeShapeStyle(raw: string): ShapeStyleState {
  try {
    const parsed = JSON.parse(raw) as Partial<ShapeStyleState>;
    const shape =
      parsed.shape && (ALL_SHAPE_TYPES as string[]).includes(parsed.shape)
        ? (parsed.shape as ShapeType)
        : DEFAULT_SHAPE_STYLE.shape;
    return {
      color: parsed.color ?? DEFAULT_SHAPE_STYLE.color,
      noFill: parsed.noFill ?? DEFAULT_SHAPE_STYLE.noFill,
      strokeStyle:
        (parsed.strokeStyle as ShapeStrokeStyle) ??
        DEFAULT_SHAPE_STYLE.strokeStyle,
      strokeWidth:
        typeof parsed.strokeWidth === "number"
          ? parsed.strokeWidth
          : DEFAULT_SHAPE_STYLE.strokeWidth,
      shape,
    };
  } catch {
    return DEFAULT_SHAPE_STYLE;
  }
}

/**
 * Shape and edge style state persisted in sessionStorage via ahooks
 * useSessionStorageState, with the same API as before.
 */
export function useCanvasShapeAndEdgeStyle() {
  const [shapeStyle, setShapeStyle] = useSessionStorageState<ShapeStyleState>(
    SHAPE_STYLE_KEY,
    {
      defaultValue: DEFAULT_SHAPE_STYLE,
      deserializer: deserializeShapeStyle,
      listenStorageChange: true,
    },
  );

  const [lastEdgeStyle, setLastEdgeStyle] =
    useSessionStorageState<LastEdgeStyle>(EDGE_STYLE_KEY, {
      defaultValue: DEFAULT_EDGE_STYLE,
      listenStorageChange: true,
    });

  const s = shapeStyle ?? DEFAULT_SHAPE_STYLE;
  const e = lastEdgeStyle ?? DEFAULT_EDGE_STYLE;

  const activeShapeStrokeColor = useMemo(
    () => SHAPE_COLORS.find((c) => c.value === s.color)?.swatchHex ?? s.color,
    [s.color],
  );

  return {
    activeShapeColor: s.color,
    activeShapeStrokeColor,
    activeShapeNoFill: s.noFill,
    activeShapeStrokeStyle: s.strokeStyle,
    activeShapeStrokeWidth: s.strokeWidth,
    lastPlacedShape: s.shape,
    // Toolbar/global setter: updates shape type and persists full style.
    setLastPlacedShape: (shape: ShapeType) =>
      setShapeStyle((prev) => ({ ...(prev ?? DEFAULT_SHAPE_STYLE), shape })),
    /**
     * Single updater for shape style (toolbar and node editing).
     * Persists color/fill/stroke fields but never overwrites the stored
     * `shape` value in sessionStorage.
     */
    setShapeStyle: (updates: Partial<Omit<ShapeStyleState, "shape">>) =>
      setShapeStyle((prev) => {
        const base = prev ?? DEFAULT_SHAPE_STYLE;
        const { shape, ...rest } = base;
        return {
          ...rest,
          ...updates,
          // Always carry forward the previous/global shape selection.
          shape,
        };
      }),
    lastEdgeStyle: e,
    setLastEdgeStyle,
  };
}
